import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { usePoTemplate, useUpdatePurchaseOrder } from '@/hooks/usePurchaseOrders'
import { draftFromDetail, draftProblem, draftToPayload, type PoDraft } from '@/lib/purchaseOrderDraft'
import type { PurchaseOrderDetail } from '@/types/purchaseOrders'
import { PurchaseOrderForm } from './PurchaseOrderForm'

interface PurchaseOrderEditorDialogProps {
  /** The order to edit; null closes the dialog. */
  po: PurchaseOrderDetail | null
  onClose: () => void
}

/**
 * Edit an open purchase order. A draft is simply saved; a SENT order has
 * already gone to the supplier, so the change needs a reason and the server
 * keeps it as the next revision rather than overwriting what was sent.
 */
export function PurchaseOrderEditorDialog({ po, onClose }: PurchaseOrderEditorDialogProps) {
  const [draft, setDraft] = useState<PoDraft | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const update = useUpdatePurchaseOrder()
  const isRevision = po?.status === 'SENT'
  // A sent order keeps the template it went out with, custom fields included.
  const template = usePoTemplate()
  const customFieldDefs = (po?.templateSnapshot ?? template.data?.layout)?.customFields ?? []

  // Keyed on the order and its revision, not the object: a background refetch
  // hands back a new object for the same order, and must not wipe the edit.
  useEffect(() => {
    setDraft(po ? draftFromDetail(po) : null)
    setReason('')
    setError(null)
  }, [po?.id, po?.revision])

  function save() {
    if (!po || !draft) return
    const problem = draftProblem(draft) ?? (isRevision && !reason.trim() ? 'Give a reason for changing a sent order.' : null)
    if (problem) return setError(problem)
    setError(null)
    update.mutate(
      { id: po.id, payload: { ...draftToPayload(draft, customFieldDefs), reason: isRevision ? reason.trim() : undefined } },
      {
        onSuccess: onClose,
        onError: (e) => setError(e instanceof Error ? e.message : 'The order could not be saved.'),
      },
    )
  }

  return (
    <Dialog open={po !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-6xl'>
        <DialogHeader>
          <DialogTitle>Edit {po?.number}</DialogTitle>
          {isRevision && (
            <DialogDescription>
              This order has been sent. Saving creates revision {(po?.revision ?? 0) + 1}; the current
              one stays in the history.
            </DialogDescription>
          )}
        </DialogHeader>

        {draft && <PurchaseOrderForm draft={draft} onChange={setDraft} customFieldDefs={customFieldDefs} />}

        {isRevision && (
          <div className='space-y-1'>
            <Label htmlFor='po-revision-reason' className='text-sm font-semibold text-gray-900'>
              Reason for change *
            </Label>
            <Textarea
              id='po-revision-reason'
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g. Supplier confirmed a later delivery date'
            />
          </div>
        )}

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : isRevision ? `Save as revision ${(po?.revision ?? 0) + 1}` : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
